import React,{useState,useEffect} from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import {useApp} from '../contexts/AppContext';
import {useAuth} from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {getRandomQuestions,quizDatabase} from '../data/quizDatabase';
const {FiClock,FiCheck,FiX,FiArrowRight,FiAward,FiRefreshCw,FiHome,FiInfo,FiCheckCircle,FiXCircle}=FiIcons;
const QuizPage=()=> {
const {subject}=useParams();
const navigate=useNavigate();
const {addNotification}=useApp();
const {user,updateUser}=useAuth();
const [currentQuestion,setCurrentQuestion]=useState(0);
const [selectedAnswer,setSelectedAnswer]=useState(null);
const [showAnswer,setShowAnswer]=useState(false);
const [score,setScore]=useState(0);
const [timeLeft,setTimeLeft]=useState(30);
const [quizCompleted,setQuizCompleted]=useState(false);
const [userAnswers,setUserAnswers]=useState([]);
const [questions,setQuestions]=useState([]);
const [loading,setLoading]=useState(true);
// Initialize quiz with random questions from the database
useEffect(()=> {
const loadQuestions=()=> {
try {
const randomQuestions=getRandomQuestions(subject,10);
if (randomQuestions.length > 0) {
setQuestions(randomQuestions);
} else {
const fallbackQuestions=getRandomQuestions('mathematics',10);
setQuestions(fallbackQuestions);
}
} catch (error) {
console.error('Error loading questions:',error);
const fallbackQuestions=[
{question: 'What is 2 + 2?',options: ['3','4','5','6'],correctAnswer: 1,explanation: '2 + 2=4',difficulty: 'beginner',topic: 'Basic Math'}
];
setQuestions(fallbackQuestions);
}
setLoading(false);
};
loadQuestions();
},[subject]);
const currentQuiz=quizDatabase[subject] || quizDatabase.mathematics;
const currentQ=questions[currentQuestion];
const progress=((currentQuestion + 1) / questions.length) * 100;
useEffect(()=> {
if (!showAnswer && timeLeft > 0 && !loading) {
const timer=setTimeout(()=> setTimeLeft(timeLeft - 1),1000);
return ()=> clearTimeout(timer);
} else if (timeLeft===0 && !showAnswer && !loading) {
handleAnswerSubmit();
}
},[timeLeft,showAnswer,loading]);
const handleAnswerSelect=(answerIndex)=> {
if (!showAnswer) {
setSelectedAnswer(answerIndex);
}
};
const handleAnswerSubmit=()=> {
if (selectedAnswer===null && timeLeft > 0) return;
const isCorrect=selectedAnswer===questions[currentQuestion].correctAnswer;
const newAnswer={questionIndex: currentQuestion,selectedAnswer,isCorrect,timeSpent: 30 - timeLeft};
setUserAnswers([...userAnswers,newAnswer]);
setShowAnswer(true);
if (isCorrect) {
setScore(score + 1);
}
};
const handleNextQuestion=()=> {
if (currentQuestion < questions.length - 1) {
setCurrentQuestion(currentQuestion + 1);
setSelectedAnswer(null);
setShowAnswer(false);
setTimeLeft(30);
} else {
setQuizCompleted(true);
const finalScore=score + (selectedAnswer===questions[currentQuestion].correctAnswer ? 1 : 0);
const percentage=Math.round((finalScore / questions.length) * 100);
const pointsEarned=finalScore * 20;
updateUser({points: (user?.points || 0) + pointsEarned});
addNotification({
type: 'success',
title: 'Quiz Completed!',
message: `You scored ${percentage}% and earned ${pointsEarned} points!`
});
}
};
const handleRestartQuiz=()=> {
setCurrentQuestion(0);
setSelectedAnswer(null);
setShowAnswer(false);
setScore(0);
setTimeLeft(30);
setQuizCompleted(false);
setUserAnswers([]);
const newQuestions=getRandomQuestions(subject,10);
setQuestions(newQuestions);
};
const handleBackToStudyRoom=()=> {
navigate('/study-room');
};
if (loading) {
return (
<div className="flex items-center justify-center min-h-screen">
<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
</div>
);
}
if (quizCompleted) {
const finalScore=userAnswers.filter(answer=> answer.isCorrect).length;
const percentage=Math.round((finalScore / questions.length) * 100);
const pointsEarned=finalScore * 20;
return (
<div className="px-4 py-4">
<motion.div initial={{opacity: 0,scale: 0.8}} animate={{opacity: 1,scale: 1}} className="max-w-2xl mx-auto w-full" >
<div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 text-center">
<SafeIcon icon={FiAward} className="w-16 h-16 mx-auto mb-4 text-yellow-500 animate-bounce-gentle" />
<h1 className="text-2xl font-bold text-gray-800 mb-4">Quiz Complete!</h1>
<div className="grid grid-cols-3 gap-4 mb-6">
<div className="bg-blue-50 rounded-lg p-4">
<div className="text-xl font-bold text-blue-600">{finalScore}/{questions.length}</div>
<div className="text-sm text-gray-600">Correct Answers</div>
</div>
<div className="bg-green-50 rounded-lg p-4">
<div className="text-xl font-bold text-green-600">{percentage}%</div>
<div className="text-sm text-gray-600">Score</div>
</div>
<div className="bg-yellow-50 rounded-lg p-4">
<div className="text-xl font-bold text-yellow-600">{pointsEarned}</div>
<div className="text-sm text-gray-600">Points Earned</div>
</div>
</div>
<div className="flex space-x-4 justify-center">
<button onClick={handleRestartQuiz} className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-600 transition-colors" >
<SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
<span>Try Again</span>
</button>
<button onClick={()=> navigate('/study-room')} className="flex items-center space-x-2 bg-gray-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-600 transition-colors" >
<SafeIcon icon={FiHome} className="w-5 h-5" />
<span>Back to Study Room</span>
</button>
</div>
</div>
</motion.div>
</div>
);
}
return (
<div className="px-4 py-4">
<div className="max-w-4xl mx-auto space-y-4 w-full">
{/* Question Header */}
<motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 w-full" >
<div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
<div className="flex-1">
<h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
{currentQ.question}
</h2>
<div className="flex items-center gap-2">
<span className="text-sm text-gray-500"> Question {currentQuestion + 1} of {questions.length} </span>
<div className="h-1 w-1 bg-gray-300 rounded-full"></div>
<span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
</div>
</div>
<div className="flex items-center gap-4">
<div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg">
<SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600" />
<span className={`font-medium ${timeLeft <=10 ? 'text-red-600' : 'text-blue-600'}`}>
{timeLeft}s
</span>
</div>
<div className="text-center">
<div className="text-2xl font-bold text-primary-600">{score}</div>
<div className="text-xs text-gray-500">Score</div>
</div>
</div>
</div>
</motion.div>
{/* Answer Options */}
<motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 w-full" >
<div className="space-y-4">
{currentQ.options.map((option,index)=> {
let buttonClass="w-full p-4 text-left border-2 rounded-lg transition-all";
let iconElement=null;
if (showAnswer) {
if (index===currentQ.correctAnswer) {
buttonClass +=" border-green-500 bg-green-50 text-green-800";
iconElement=<SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />;
} else if (index===selectedAnswer && index !==currentQ.correctAnswer) {
buttonClass +=" border-red-500 bg-red-50 text-red-800";
iconElement=<SafeIcon icon={FiXCircle} className="w-5 h-5 text-red-600" />;
} else {
buttonClass +=" border-gray-200 bg-gray-50 text-gray-600";
}
} else {
if (selectedAnswer===index) {
buttonClass +=" border-primary-500 bg-primary-50 text-primary-800";
} else {
buttonClass +=" border-gray-200 hover:border-primary-300 hover:bg-primary-50";
}
}
return (
<button key={index} onClick={()=> handleAnswerSelect(index)} disabled={showAnswer} className={buttonClass} >
<div className="flex items-center gap-3">
<div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-current flex items-center justify-center font-semibold">
{String.fromCharCode(65 + index)}
</div>
<span className="text-base flex-1">{option}</span>
{iconElement && <div className="flex-shrink-0">{iconElement}</div>}
</div>
</button>
);
})}
</div>
{/* Explanation Section - Shows after answer is submitted */}
<AnimatePresence>
{showAnswer && currentQ.explanation && (
<motion.div initial={{opacity: 0,height: 0}} animate={{opacity: 1,height: 'auto'}} exit={{opacity: 0,height: 0}} transition={{duration: 0.3}} className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" >
<div className="flex items-start space-x-3">
<div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
<SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600" />
</div>
<div className="flex-1">
<h4 className="text-sm font-semibold text-blue-800 mb-2">
{selectedAnswer===currentQ.correctAnswer ? 'Correct! ✅' : 'Explanation 💡'}
</h4>
<p className="text-sm text-blue-700 leading-relaxed">
{currentQ.explanation}
</p>
{selectedAnswer !==currentQ.correctAnswer && (
<div className="mt-2 p-2 bg-green-100 border border-green-200 rounded">
<p className="text-xs text-green-700">
<strong>Correct Answer:</strong> {String.fromCharCode(65 + currentQ.correctAnswer)}) {currentQ.options[currentQ.correctAnswer]}
</p>
</div>
)}
</div>
</div>
</motion.div>
)}
</AnimatePresence>
{/* Action Buttons */}
<div className="flex justify-between mt-6">
<button onClick={()=> navigate('/study-room')} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" >
Exit Quiz
</button>
{!showAnswer ? (
<button onClick={handleAnswerSubmit} disabled={selectedAnswer===null} className={`px-6 py-2 rounded-lg font-medium transition-colors ${selectedAnswer !==null ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} >
Submit Answer
</button>
) : (
<button onClick={handleNextQuestion} className="flex items-center space-x-2 bg-primary-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-600 transition-colors" >
<span>{currentQuestion < questions.length - 1 ? 'Next Question' : 'Finish Quiz'}</span>
<SafeIcon icon={FiArrowRight} className="w-4 h-4" />
</button>
)}
</div>
</motion.div>
</div>
</div>
);
};
export default QuizPage;